import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Phone,
  ArrowLeftRight,
  BarChart3,
  ShoppingBag,
  PanelLeftClose,
  PanelLeft,
  type LucideIcon,
} from 'lucide-react';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

const navItems: NavItem[] = [
  { name: 'Command Hub', href: '/', icon: LayoutDashboard },
  { name: 'Traffic & Usage', href: '/usage', icon: BarChart3 },
  { name: 'Phone Numbers', href: '/dids', icon: Phone },
  { name: 'Port Orders', href: '/porting', icon: ArrowLeftRight },
  { name: 'Order History', href: '/order-history', icon: ShoppingBag },
];

interface SidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed = false, onCollapsedChange }: SidebarProps) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapsedChange?.(newState);
  };

  return (
    <aside
      className={cn(
        'bg-slate-800 text-white flex flex-col h-screen sticky top-0 transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-56'
      )}
    >
      {/* Logo Identity Block */}
      <div className="px-6 py-8 mb-4 border-b border-slate-700/50 flex items-center justify-center gap-3">
        {isCollapsed ? (
          <img
            src="/logo-icon.png"
            alt="telviaIQ"
            className="w-12 h-12"
          />
        ) : (
          <img
            src="/logo-white.png"
            alt="telviaIQ"
            className="h-9"
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1">
        {navItems.map((item) => {
          const isActive =
            item.href === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.href);

          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative',
                isActive
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              )}
            >
              <item.icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
              {!isCollapsed && (
                <span className="text-sm font-medium">{item.name}</span>
              )}
              {isCollapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                  {item.name}
                </span>
              )}
              {isActive && !isCollapsed && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <div className="p-2 border-t border-slate-700">
        <button
          onClick={handleToggle}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
        >
          {isCollapsed ? (
            <PanelLeft size={20} strokeWidth={1.5} />
          ) : (
            <>
              <PanelLeftClose size={20} strokeWidth={1.5} />
              <span className="text-sm font-medium">Collapse Sidebar</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
